import React, { useEffect, useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import { APP_VERSION } from '../../lib/version'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

const PERIODS = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: '6months', label: '6 months' },
  { id: 'year', label: 'Year' },
  { id: '2years', label: '2 years' },
] as const

type PeriodId = typeof PERIODS[number]['id']

type Allocation = {
  commission: number
  goldDiggers: number
  dailyPool: number
  aureus: number
  uhhf: number
  sun: number
  total: number
}

type CurrencyBlock = {
  currency: string
  purchases: number
  shares: number
  sale: number
  saleUsd: number
  zarReceived: number
  zarReceivedPurchases: number
  platformRatePurchases: number
  platformRateUsd: number
  allocation: Allocation
}

type Report = {
  label: string
  timezone: string
  start: string
  end: string
  basis: string
  currencies: {
    USDT: CurrencyBlock
    ZAR: CurrencyBlock
    USD_BANK: CurrencyBlock
    OTHER: CurrencyBlock
  }
}

const SECTOR_ROWS: Array<{ key: keyof Allocation; label: string; rate: string }> = [
  { key: 'commission', label: 'Commission', rate: '15%' },
  { key: 'goldDiggers', label: 'Gold Diggers', rate: '10%' },
  { key: 'dailyPool', label: 'Daily Performance Pool', rate: '10%' },
  { key: 'aureus', label: 'Aureus', rate: '35%' },
  { key: 'uhhf', label: 'UHHF', rate: '10%' },
  { key: 'sun', label: 'SUN', rate: '20%' },
]

function money(currency: string, value: number) {
  const formatted = (Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  if (currency === 'ZAR') return `R${formatted}`
  return `$${formatted}`
}

function shares(value: number) {
  return (Number(value) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function formatWhen(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function CurrencyCard({
  title,
  unit,
  block,
}: {
  title: string
  unit: string
  block: CurrencyBlock
}) {
  return (
    <section style={cardStyle}>
      <h3 style={{ margin: '0 0 4px', color: '#F5D76E', fontSize: '18px' }}>{title}</h3>
      <p style={{ margin: '0 0 16px', color: '#9CA3AF', fontSize: '13px' }}>
        {block.purchases.toLocaleString()} purchases · {shares(block.shares)} shares
      </p>
      <div style={totalStyle}>
        <span>Sale value in {unit}</span>
        <strong>{money(block.currency === 'ZAR' ? 'ZAR' : 'USDT', block.sale)}</strong>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
        <tbody>
          {SECTOR_ROWS.map((sector) => (
            <tr key={sector.key} style={{ borderTop: '1px solid #1F2937' }}>
              <td style={cellStyle}>{sector.label}</td>
              <td style={{ ...cellStyle, color: '#9CA3AF', width: '52px' }}>{sector.rate}</td>
              <td style={{ ...cellStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {money(block.currency === 'ZAR' ? 'ZAR' : 'USDT', block.allocation[sector.key])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {block.currency === 'ZAR' && block.zarReceived > 0 && (
        <p style={noteStyle}>
          Rand received on {block.zarReceivedPurchases.toLocaleString()} payments: {money('ZAR', block.zarReceived)}.
          That figure includes the bank fee. The split above is the share sale, and the fee is not part of it.
        </p>
      )}
      {block.currency === 'ZAR' && block.platformRatePurchases > 0 && (
        <p style={noteStyle}>
          {block.platformRatePurchases.toLocaleString()} ZAR purchases ({money('USDT', block.platformRateUsd)} on the ledger)
          had no stored rate. They are included in the split at R17 per USDT.
        </p>
      )}
    </section>
  )
}

export function RevenueAllocationReport() {
  const [period, setPeriod] = useState<PeriodId>('month')
  const [report, setReport] = useState<Report | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/revenue-allocation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ period }),
        })
        const body = await response.json()
        if (!response.ok || !body.success) {
          throw new Error(body.error || 'The report could not be loaded.')
        }
        if (!cancelled) setReport(body.data)
      } catch (err) {
        if (!cancelled) {
          setReport(null)
          setError(err instanceof Error ? err.message : 'The report could not be loaded.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [period])

  const extra = report
    ? [report.currencies.USD_BANK, report.currencies.OTHER].filter((block) => block.purchases > 0)
    : []

  return (
    <div>
      <h2 style={{ margin: '0 0 8px', color: '#F5D76E' }}>Share sale allocation</h2>
      <p style={{ margin: '0 0 16px', color: '#D1D5DB', maxWidth: '760px', lineHeight: 1.5 }}>
        Active share sales in USDT and ZAR, then the amount available to each sector.
        This page only displays the split. It does not pay commission, pools, or entities.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        {PERIODS.map((item) => {
          const active = item.id === period
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setPeriod(item.id)}
              style={{
                background: active ? '#F5D76E' : '#111827',
                color: active ? '#111827' : '#E5E7EB',
                border: '1px solid #374151',
                borderRadius: '999px',
                padding: '8px 14px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {item.label}
            </button>
          )
        })}
      </div>
      {loading && <p style={{ color: '#9CA3AF' }}>Loading sales for this period…</p>}
      {error && <p style={{ color: '#FCA5A5' }}>{error}</p>}
      {report && !loading && (
        <>
          <p style={{ color: '#9CA3AF', fontSize: '13px', marginTop: 0 }}>
            {report.label}: {formatWhen(report.start)} → {formatWhen(report.end)} ({report.timezone})
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <CurrencyCard title="USDT" unit="USDT" block={report.currencies.USDT} />
            <CurrencyCard title="ZAR" unit="ZAR" block={report.currencies.ZAR} />
          </div>
          {extra.map((block) => (
            <div key={block.currency} style={{ marginTop: '16px' }}>
              <CurrencyCard
                title={block.currency === 'USD_BANK' ? 'USD bank transfers' : 'Other cash sales'}
                unit="USD"
                block={block}
              />
            </div>
          ))}
          <p style={{ ...noteStyle, marginTop: '16px' }}>{report.basis}</p>
          <p style={noteStyle}>
            Version {APP_VERSION}. A new secure version has been deployed. Please refresh the page.
            If anything appears incorrect, clear your cache and reload to ensure you are using the latest version.
          </p>
        </>
      )}
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#0B0B0B',
  border: '1px solid #1F2937',
  borderRadius: '12px',
  padding: '16px',
}

const totalStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px',
  color: '#E5E7EB',
  fontSize: '15px',
}

const cellStyle: React.CSSProperties = {
  padding: '10px 0',
  color: '#E5E7EB',
  fontSize: '14px',
}

const noteStyle: React.CSSProperties = {
  color: '#9CA3AF',
  fontSize: '13px',
  lineHeight: 1.5,
  margin: '12px 0 0',
}
